import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  Download,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from "lucide-react";
import Layout from "./layout";
import { examineeGroupData } from "../data/examineeGroupMockData";
import ExamineeGroupModal from "../Modal/ExamineeGroupModal";
import EditMemberModal from "../Modal/EditMemberModal";
import GroupingModal from "../Modal/GroupingModal";
import { memberAPI, healthRecordsAPI } from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: 'Noto Sans Thai', sans-serif;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: #00b5b8;
    border-bottom: none;
    padding-top: 0.75rem;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: white;
    font-size: 0.75rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
  }

  .react-datepicker__day {
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
    font-size: 0.75rem;
    color: #374151;
    border-radius: 0.25rem;
    transition: all 0.2s;

    &:hover {
      background-color: #e0f7f8;
      color: #00b5b8;
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00b5b8;
    color: white;
    font-weight: 500;

    &:hover {
      background-color: #0891b2;
    }
  }

  .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  .react-datepicker__day--today {
    font-weight: 600;
    color: #00b5b8;
    background-color: #e0f7f8;

    &:hover {
      background-color: #b3e5e8;
    }
  }

  .react-datepicker__day--outside-month {
    color: #d1d5db;
  }

  .react-datepicker__navigation {
    top: 0.75rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
`;

const UploadAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const UploadArea = styled.div`
  width: 100%;
  max-width: 500px;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;

  &:hover {
    border-color: #00b5b8;
    background-color: #e0f7f8;
    transform: scale(1.02);
  }
`;

const UploadText = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const UploadInput = styled.input`
  display: none;
`;

const DownloadExampleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00b5b8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;

  &:hover {
    color: #0891b2;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #00b5b8;
  cursor: pointer;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const AddGroupButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const ResetButton = styled.button`
  background-color: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #333333;
  }
`;

const GroupingButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }
`;

const RightButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00b5b8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    color: #0891b2;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background-color: #f9fafb;
  white-space: nowrap;
  min-width: ${(props) => props.minWidth || "auto"};
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  font-size: 12px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  text-align: center;
  min-width: ${(props) => props.minWidth || "auto"};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: #374151;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${(props) => (props.active ? "#1976D2" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#1976D2")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${(props) => (props.active ? "none" : "underline")};

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? "#1565C0" : "#E3F2FD")};
  }

  &:disabled {
    color: #9e9e9e;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const _fmtDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const _today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
const _7dAgo = (() => { const d = new Date(2025, 0, 1); d.setHours(0,0,0,0); return d; })();
const ExamineeGroup = () => {
  const [searchFilters, setSearchFilters] = useState({
    keyword: "",
    branch: "",
    startDate: _7dAgo,
    endDate: _today,
  });

  // ค่าที่ใช้ filter จริง — เริ่มต้น default 14 ส.ค. 2568
  const [activeFilters, setActiveFilters] = useState({
    keyword: "",
    branch: "",
    startDate: _fmtDate(_7dAgo),
    endDate: _fmtDate(_today),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminStates, setAdminStates] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGroupingModalOpen, setIsGroupingModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchMembers({
      dateStart: _fmtDate(_7dAgo),
      dateStop: _fmtDate(_today),
    });
    fetchCompanies();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const fetchCompanies = async () => {
    try {
      const campaignAPI = require("../services/api").campaignAPI;
      const response = await campaignAPI.getList(1, "");
      if (response?.list && Array.isArray(response.list)) {
        setCompanies(response.list);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  };

  const fetchMembers = async (apiFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { keywordName: "", ...apiFilters };

      const firstPageResponse = await memberAPI.getList({ pageNow: 1, ...params });

      let allMemberData = [];

      if (Array.isArray(firstPageResponse.list)) {
        allMemberData = [...firstPageResponse.list];

        if (firstPageResponse.result) {
          const totalPages = parseInt(firstPageResponse.pageAmount) || 1;
          if (totalPages > 1) {
            const pagePromises = [];
            for (let page = 2; page <= totalPages; page++) {
              pagePromises.push(memberAPI.getList({ pageNow: page, ...params }));
            }
            const responses = await Promise.all(pagePromises);
            responses.forEach((response) => {
              if (response.list && Array.isArray(response.list)) {
                allMemberData = [...allMemberData, ...response.list];
              }
            });
          }
        }
      } else {
        console.warn("API response format unknown");
        allMemberData = [];
      }

      let memberData = allMemberData;

      memberData = memberData.map((member) => ({
        ...member,
        campaign: member.memberCompany || "ทั่วไป",
      }));

      setMembers(memberData);

      const initialAdminStates = memberData.reduce((acc, item) => {
        acc[item.id] = item.level === "2";
        return acc;
      }, {});
      setAdminStates(initialAdminStates);
    } catch (err) {
      console.error("Error fetching members:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentData = members.filter((member) => {
    if (activeFilters.keyword && activeFilters.keyword.trim() !== "") {
      const keywordInput = activeFilters.keyword.toLowerCase().trim();
      const keywords = keywordInput.split(/\s+/).filter((k) => k.length > 0);

      const name = (member.name || "").toLowerCase();
      const lastname = (member.lastname || "").toLowerCase();
      const nameprefix = (member.nameprefix || "").toLowerCase();
      const fullName = `${nameprefix} ${name} ${lastname}`.toLowerCase();
      const username = (member.username || "").toLowerCase();
      const nid = (member.nid || "").toLowerCase();
      const searchableText = `${fullName} ${username} ${nid}`;
      const matchKeyword = keywords.every((kw) => searchableText.includes(kw));

      if (!matchKeyword) return false;
    }

    if (activeFilters.branch && activeFilters.branch.trim() !== "") {
      const selectedCompany = activeFilters.branch.trim();
      const memberCompany = (member.campaign || "").trim();

      if (memberCompany !== selectedCompany) return false;
    }

    return true;
  });

  const totalFilteredItems = currentData.length;
  const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = currentData.slice(startIndex, endIndex);

  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    const newFilters = {
      keyword: searchFilters.keyword,
      branch: searchFilters.branch,
      startDate: _fmtDate(searchFilters.startDate),
      endDate: _fmtDate(searchFilters.endDate),
    };
    setActiveFilters(newFilters);
    setCurrentPage(1);
    fetchMembers({
      dateStart: newFilters.startDate,
      dateStop: _fmtDate(searchFilters.endDate),
    });
  };

  const handleReset = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    setSearchFilters({ keyword: "", branch: "", startDate: _7dAgo, endDate: today });
    setActiveFilters({ keyword: "", branch: "", startDate: _fmtDate(_7dAgo), endDate: _fmtDate(today) });
    setCurrentPage(1);
    fetchMembers({ dateStart: _fmtDate(_7dAgo), dateStop: _fmtDate(today) });
  };

  const handleDownloadCSV = () => {
    const getGender = (gender) => {
      const genderStr = String(gender ?? "").trim();
      if (
        genderStr === "1" ||
        genderStr.toLowerCase() === "male" ||
        genderStr === "ชาย"
      )
        return "ชาย";
      if (
        genderStr === "0" ||
        genderStr.toLowerCase() === "female" ||
        genderStr === "หญิง"
      )
        return "หญิง";
      if (genderStr === "") return "-";
      return "อื่นๆ";
    };

    const calculateAge = (birthday) => {
      if (!birthday) return "-";
      let birthDate = new Date(birthday);
      if (isNaN(birthDate.getTime())) {
        return "-";
      }

      const today = new Date();
      let birthYear = birthDate.getFullYear();

      if (birthYear >= 2500) {
        birthYear = birthYear - 543;
        birthDate = new Date(
          birthYear,
          birthDate.getMonth(),
          birthDate.getDate(),
        );
      }

      let age = today.getFullYear() - birthYear;
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age < 0 || age > 120) {
        return "-";
      }

      return age;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "-";
      const date = dateString.split(" ")[0];
      const parts = date.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return date;
    };

    const headers = [
      "ลำดับ",
      "วันที่สมัคร",
      "เลขบัตรประชาชน",
      "ชื่อ-สกุล",
      "บริษัท",
      "แผนก",
      "ตำแหน่ง",
    ];

    const dataToExport = currentData;

    const rows = dataToExport.map((member, index) => {
      const rowNumber = index + 1;
      const registrationDate = formatDate(
        member.mddateadd || member.registrationDate || member.dateadd,
      );
      const nid = member.nid ? `="${member.nid}"` : "-"; // ="..." บังคับ Excel แสดงเป็น text ป้องกัน scientific notation
      const fullName = member.name
        ? `${member.nameprefix || ""} ${member.name} ${member.lastname || ""}`.trim()
        : "-";
      const company = member.campaign || "-";
      const department = member.position || "-";
      const position = member.job || "-";

      return [
        rowNumber,
        registrationDate,
        nid,
        fullName,
        company,
        department,
        position,
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      )
      .join("\n");

    const BOM = "\uFEFF"; // BOM สำหรับ UTF-8 เพื่อให้ Excel เปิดภาษาไทยได้ถูกต้อง
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `รายชื่อสมาชิก_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExample = () => {
    console.log("ดาวน์โหลดตัวอย่าง v1");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log("อัปโหลดไฟล์:", file.name);
    }
  };

  const handleUploadClick = () => {
    document.getElementById("file-upload").click();
  };

  const handleAdminToggle = async (id) => {
    const newState = !adminStates[id];

    setAdminStates((prev) => ({
      ...prev,
      [id]: newState,
    }));

    try {
      const member = members.find((m) => m.id === id);
      if (member) {
        await memberAPI.update({
          ...member,
          level: newState ? "2" : "1",
        });
      }
    } catch (err) {
      console.error("Error updating admin status:", err);
      setAdminStates((prev) => ({
        ...prev,
        [id]: !newState,
      }));
      alert("เกิดข้อผิดพลาดในการอัพเดทสถานะ: " + err.message);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      if (memberData.id) {
        await memberAPI.update(memberData);
      } else {
        await memberAPI.add(memberData);
      }
      fetchMembers({ dateStart: activeFilters.startDate, dateStop: activeFilters.endDate });
      setIsModalOpen(false);
      setEditingMember(null);
    } catch (err) {
      console.error("Error saving member:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
    }
  };

  const handleUpdateMember = async (memberData) => {
    try {
      const response = await memberAPI.update(memberData);

      if (response.result) {
        // เก็บชื่อบริษัทที่อัพเดตลง localStorage เพื่อให้หน้าสมุดสุขภาพอ่านได้
        if (memberData.campaignid) {
          const selectedCompany = companies.find(
            (c) => c.id === memberData.campaignid,
          );
          const companyName = selectedCompany?.name;
          const memberId =
            editingMember?.memberid || editingMember?.id || memberData.id;

          if (companyName && memberId) {
            const updatedCompanies = JSON.parse(
              localStorage.getItem("updatedMemberCompanies") || "{}",
            );
            updatedCompanies[memberId] = companyName;
            localStorage.setItem(
              "updatedMemberCompanies",
              JSON.stringify(updatedCompanies),
            );

            // Save company ลง health_records ของเรา เพื่อให้ public report ดึงได้ถูกต้อง
            try {
              await healthRecordsAPI.updateCompanyByHn(
                memberId,
                companyName,
                activeFilters.startDate || null,
                activeFilters.endDate || null,
              );
            } catch (e) {
              // ไม่ block flow หลัก
            }
          }
        }

        await fetchMembers({ dateStart: activeFilters.startDate, dateStop: activeFilters.endDate });

        setIsEditModalOpen(false);
        setEditingMember(null);

        alert("บันทึกข้อมูลสำเร็จ");
      } else {
        const errorMsg =
          response.responseText || "เกิดข้อผิดพลาดในการอัพเดทข้อมูล";
        console.error("API returned error:", errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("Error updating member:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingMember(null);
  };

  const handleSaveGrouping = async (groupingData) => {
    try {
      alert("จัดกลุ่มผู้รับการตรวจสำเร็จ");
      fetchMembers({ dateStart: activeFilters.startDate, dateStop: activeFilters.endDate });
    } catch (err) {
      console.error("Error grouping members:", err);
      alert("เกิดข้อผิดพลาดในการจัดกลุ่ม: " + err.message);
    }
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        {loading && (
          <div
            style={{ textAlign: "center", padding: "1rem", fontSize: "12px" }}
          >
            กำลังโหลดข้อมูล...
          </div>
        )}
        {error && (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#EF4444",
              fontSize: "12px",
            }}
          >
            เกิดข้อผิดพลาด: {error}
          </div>
        )}

        <FilterSection style={{ marginBottom: "1rem" }}>
          <FilterGroup style={{ flex: "1", minWidth: "250px" }}>
            <Label>ค้นหาจาก ชื่อ-สกุล หรือ เลขบัตร</Label>
            <TextInput
              type="text"
              value={searchFilters.keyword}
              onChange={(e) => handleFilterChange("keyword", e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "200px" }}>
            <Label>บริษัท</Label>
            <SelectInput
              value={searchFilters.branch}
              onChange={(e) => handleFilterChange("branch", e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {companies
                .filter((company) => company.name && company.name !== "-")
                .map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
            </SelectInput>
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "180px" }}>
            <Label>วันที่เริ่มต้น</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "180px" }}>
            <Label>ถึงวันที่</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={searchFilters.startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <SearchButton onClick={handleSearch}>ค้นหา</SearchButton>
        </FilterSection>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            marginBottom: "1.5rem",
          }}
        >
          <DownloadButton onClick={handleDownloadCSV}>
            <Download size={16} />
            ดาวน์โหลด CSV
          </DownloadButton>

          <GroupingButton onClick={() => setIsGroupingModalOpen(true)}>
            จัดกลุ่มผู้รับการตรวจ
          </GroupingButton>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <RightButtonGroup>
            <PaginationContainer style={{ marginTop: "0.5rem" }}>
              <PaginationButton
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                previous
              </PaginationButton>
              {[...Array(totalPages)].map((_, index) => (
                <PaginationButton
                  key={index + 1}
                  active={currentPage === index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </PaginationButton>
              ))}
              <PaginationButton
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                next
              </PaginationButton>
            </PaginationContainer>
          </RightButtonGroup>
        </div>
      </PageContainer>

      <div style={{ overflowX: "auto" }}>
        <Table>
          <thead>
            <tr>
              <TableHeader minWidth="60px">ที่</TableHeader>
              <TableHeader minWidth="120px">วันที่ตรวจ</TableHeader>
              <TableHeader minWidth="110px">เวลา</TableHeader>
              <TableHeader minWidth="140px">บัตรประชาชน</TableHeader>
              <TableHeader minWidth="150px">ชื่อ-สกุล</TableHeader>
              <TableHeader minWidth="120px">บริษัท</TableHeader>
              <TableHeader minWidth="100px">แผนก</TableHeader>
              <TableHeader minWidth="100px">ตำแหน่ง</TableHeader>
              <TableHeader minWidth="80px">admin</TableHeader>
              <TableHeader minWidth="80px">แก้ไข</TableHeader>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    textAlign: "center",
                    padding: "3rem 1rem",
                    color: "#9ca3af",
                    fontSize: "14px",
                  }}
                >
                  ไม่พบรายการ
                </td>
              </tr>
            )}
            {paginatedData.map((row, index) => {
              const formatDate = (dateString) => {
                if (!dateString) return "-";
                const date = dateString.split(" ")[0];
                const parts = date.split("-");
                if (parts.length === 3) {
                  return `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
                return date;
              };

              const getGender = (gender) => {
                if (gender === "1") return "ชาย";
                if (gender === "2") return "หญิง";
                return "อื่นๆ";
              };

              const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

              return (
                <tr key={(currentPage - 1) * itemsPerPage + index}>
                  <TableCell minWidth="60px">{rowNumber}</TableCell>
                  <TableCell minWidth="120px">
                    {formatDate(row.mddateadd || row.registrationDate || row.dateadd)}
                  </TableCell>
                  <TableCell minWidth="110px">
                    {(row.mddateadd || row.registrationDate || row.dateadd || "").split(" ")[1]?.slice(0, 8) || "-"}
                  </TableCell>
                  <TableCell minWidth="100px">{row.nid || "-"}</TableCell>
                  <TableCell minWidth="150px">
                    {row.name
                      ? `${row.nameprefix || ""} ${row.name} ${row.lastname || ""}`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell minWidth="120px">{row.campaign || "-"}</TableCell>
                  <TableCell minWidth="100px">{row.position || "-"}</TableCell>
                  <TableCell minWidth="100px">{row.job || "-"}</TableCell>
                  <TableCell minWidth="80px">
                    <input
                      type="checkbox"
                      checked={adminStates[row.id]}
                      disabled={true}
                      style={{
                        cursor: "not-allowed",
                        transform: "scale(1.0)",
                      }}
                    />
                  </TableCell>
                  <TableCell minWidth="80px">
                    <EditButton onClick={() => handleEditMember(row)}>
                      <Edit2 size={12} />
                    </EditButton>
                  </TableCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <ExamineeGroupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMember}
        memberData={null}
      />

      <EditMemberModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateMember}
        memberData={editingMember}
      />

      <GroupingModal
        isOpen={isGroupingModalOpen}
        onClose={() => setIsGroupingModalOpen(false)}
        onSave={handleSaveGrouping}
      />
    </Layout>
  );
};

export default ExamineeGroup;
